import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from '../utils/cn';

type KoobInputFileProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'className' | 'type' | 'value' | 'onChange' | 'multiple'
> & {
  value: File | null;
  onValueChange: (file: File | null) => void;
  /** Main line of the empty drop zone; defaults to "Choose file". */
  placeholder?: React.ReactNode;
  /** Muted line under the placeholder (accepted formats, size limit…). */
  hint?: React.ReactNode;
  error?: boolean;
};

const formatSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

/** Single-file picker: click or drop a file, then shows it with a remove button. */
const KoobInputFile = React.forwardRef<HTMLInputElement, KoobInputFileProps>(
  (
    { value, onValueChange, placeholder, hint, error, disabled, ...props },
    ref,
  ) => {
    const { t } = useTranslation('misc');
    const [dragging, setDragging] = useState(false);

    if (value) {
      return (
        <div
          className={cn(
            'flex items-center gap-3 rounded-md border border-solid bg-background p-3',
            error ? 'border-red-500' : 'border-input',
          )}
        >
          <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-brand/10 text-brand">
            <i className="fa-light fa-file-lines text-lg" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{value.name}</div>
            <div className="text-xs text-muted-foreground">
              {formatSize(value.size)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onValueChange(null)}
            disabled={disabled}
            aria-label={t('delete')}
            className="flex size-8 items-center justify-center rounded-md border-none bg-transparent text-muted-foreground cursor-pointer hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            <i className="fa-light fa-xmark" aria-hidden />
          </button>
        </div>
      );
    }

    return (
      <label
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const file = e.dataTransfer.files?.[0];
          if (!disabled && file) onValueChange(file);
        }}
        className={cn(
          'flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-4 py-8 text-center transition-colors focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20',
          disabled
            ? 'cursor-not-allowed opacity-50 bg-gray-100'
            : 'cursor-pointer hover:border-brand hover:bg-brand/5',
          dragging ? 'border-brand bg-brand/5' : 'border-input',
          error && 'border-red-500',
        )}
      >
        <i
          className="fa-light fa-cloud-arrow-up mb-1 text-2xl text-brand"
          aria-hidden
        />
        <span className="text-sm font-medium">
          {placeholder ?? t('chooseFile')}
        </span>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
        <input
          ref={ref}
          type="file"
          disabled={disabled}
          className="sr-only"
          onChange={(e) => {
            onValueChange(e.target.files?.[0] ?? null);
            // reset so picking the same file again still fires onChange
            e.target.value = '';
          }}
          {...props}
        />
      </label>
    );
  },
);

KoobInputFile.displayName = 'KoobInputFile';

export default KoobInputFile;
